import {forwardRef, type ComponentPropsWithoutRef, type ElementType, type ReactNode} from 'react';
import {Slot, Slottable} from '@radix-ui/react-slot';
import {cn} from '../lib/utils';
import type {Testable} from '../lib/testable';
import {SpinnerIcon} from '../icons';

type AnchorOnlyProps = Omit<ComponentPropsWithoutRef<'a'>, keyof ComponentPropsWithoutRef<'button'>>;

export interface ButtonProps extends ComponentPropsWithoutRef<'button'>, AnchorOnlyProps, Testable {
	as?: 'button' | 'a';
	variant?: ButtonVariant;
	size?: ButtonSize;
	icon?: ReactNode;
	isLoading?: boolean;
	fullWidth?: boolean;
	asChild?: boolean;
}

export const Button = forwardRef<HTMLElement, ButtonProps>(({
	variant = 'primary',
	size = 'md',
	as = 'button',
	icon,
	isLoading = false,
	fullWidth = false,
	asChild = false,
	disabled = false,
	type = 'button',
	href,
	testId,
	className,
	children,
	...props
}, ref) => {
	const isAnchor = !asChild && as === 'a';
	const Component = (asChild ? Slot : isAnchor ? 'a' : 'button') as ElementType;
	const isDisabled = disabled || isLoading;
	const classes = cn(
		baseStyles,
		sizeStyles[size],
		variantStyles[variant],
		variant === 'link' && 'h-auto p-0',
		isLoading && 'pointer-events-none',
		isLoading && loadingStyles[variant],
		fullWidth && 'w-full',
		className
	);

	return (
		<Component
			ref={ref}
			data-component='Button'
			data-testid={testId}
			className={classes}
			aria-busy={isLoading || undefined}
			{...(isAnchor && {href: isDisabled ? undefined : href})}
			{...(asChild || isAnchor ? {'aria-disabled': isDisabled || undefined} : {type, disabled: isDisabled})}
			{...props}>
			{isLoading ? <SpinnerIcon aria-hidden className='animate-spin'/> : icon}
			<Slottable>{children}</Slottable>
		</Component>
	);
});

Button.displayName = 'Button';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'link' | 'danger';

export type ButtonSize = 'sm' | 'md' | 'lg';

const baseStyles =
	'group inline-flex shrink-0 cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-md font-medium ' +
	'transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-main-light ' +
	'disabled:cursor-not-allowed [&_svg]:shrink-0';

const sizeStyles: Record<ButtonSize, string> = {
	sm: 'h-7 px-4 text-xs [&_svg]:size-4',
	md: 'h-8 px-4 text-sm [&_svg]:size-4',
	lg: 'h-10 px-6 text-sm [&_svg]:size-5'
};

const variantStyles: Record<ButtonVariant, string> = {
	primary: 'border-t border-brand-main bg-brand-main text-white shadow-s enabled:hover:bg-brand-main-dark ' +
		'disabled:border-foreground-100 disabled:bg-foreground-200 disabled:text-white',
	secondary: 'border border-brand-main text-foreground-contrast shadow-s enabled:hover:bg-brand-main/20 ' +
		'disabled:border-foreground-300 disabled:text-foreground-200',
	ghost: 'text-foreground-contrast enabled:hover:bg-brand-main/20 enabled:hover:shadow-s disabled:text-foreground-200',
	link: 'text-brand-main-light underline-offset-2 hover:underline disabled:text-foreground-200 ' +
		'disabled:no-underline aria-disabled:no-underline',
	danger: 'border-t border-tone-red bg-tone-red text-white shadow-s enabled:hover:bg-tone-red/80 ' +
		'disabled:border-foreground-100 disabled:bg-foreground-200 disabled:text-white'
};

const loadingStyles: Partial<Record<ButtonVariant, string>> = {
	primary: 'disabled:border-brand-main disabled:bg-brand-main disabled:text-white',
	danger: 'disabled:border-tone-red disabled:bg-tone-red disabled:text-white'
};
